import React, { useEffect, useMemo, useState } from 'react';
import { Typography, Card, Table, Button, Modal, Form, Input, Switch, Space, message, Checkbox, Radio, Select } from 'antd';
import MainLayout from '../components/MainLayout';
import { documentTypeService } from '../services/documentTypes';
import { tenantService } from '../services/tenants';
import { getTranslatedItemTypes, getItemTypeLabel } from '../utils/translations';
import { useAuth } from '../contexts/AuthContext';
import './Dashboard.css';

const { Title } = Typography;

function DocumentTypes() {
  const { user } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [form] = Form.useForm();
  const [tenants, setTenants] = useState([]);
  const [mandatoryMode, setMandatoryMode] = useState('none');
  const canEdit = user?.role === 'admin' || user?.role === 'qhse_nke';

  const itemTypes = ['company', 'worker', 'vehicle', 'equipment', 'chemical'];

  const formatError = (err) => {
    const detail = err?.response?.data?.detail;
    if (!detail) return 'Errore';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map((d) => d.msg || d.detail || JSON.stringify(d)).join(' | ');
    if (typeof detail === 'object') return detail.msg || detail.detail || JSON.stringify(detail);
    return 'Errore';
  };

  const loadDocumentTypes = async () => {
    setLoading(true);
    try {
      const res = await documentTypeService.list({ active_only: false });
      setData(res || []);
    } catch (e) {
      message.error('Errore nel caricamento tipologie documento');
    } finally {
      setLoading(false);
    }
  };

  const loadTenants = async () => {
    try {
      const res = await tenantService.list();
      setTenants(res || []);
    } catch (e) {
      console.error('Errore nel caricamento tenant:', e);
    }
  };

  useEffect(() => {
    loadDocumentTypes();
    loadTenants();
  }, []);

  const filteredData = useMemo(() => {
    if (!search) return data;
    const term = search.toLowerCase();
    return data.filter((item) => Object.values(item || {}).some((val) => (val !== null && val !== undefined) && String(val).toLowerCase().includes(term)));
  }, [data, search]);

  const stringSorter = (key) => (a, b) => (a[key] || '').localeCompare(b[key] || '');
  const boolSorter = (key) => (a, b) => Number(a[key]) - Number(b[key]);

  const onSubmit = async (values) => {
    try {
      const payload = {
        name: values.name,
        description: values.description,
        applicable_item_types: values.applicable_item_types || [],
        mandatory_mode: values.mandatory_mode || 'none',
        mandatory_for_tenants: values.mandatory_mode === 'specific' ? (values.mandatory_for_tenants || []) : [],
        is_active: values.is_active ?? true,
      };

      if (editing) {
        await documentTypeService.update(editing.id, payload);
        message.success('Tipologia aggiornata');
      } else {
        await documentTypeService.create(payload);
        message.success('Tipologia creata');
      }
      setModalOpen(false);
      setEditing(null);
      form.resetFields();
      setMandatoryMode('none');
      loadDocumentTypes();
    } catch (e) {
      message.error(formatError(e));
    }
  };

  const handleEdit = (record) => {
    setEditing(record);
    setMandatoryMode(record.mandatory_mode || 'none');
    form.setFieldsValue({
      name: record.name,
      description: record.description,
      applicable_item_types: record.applicable_item_types || [],
      mandatory_mode: record.mandatory_mode || 'none',
      mandatory_for_tenants: record.mandatory_for_tenants || [],
      is_active: record.is_active ?? true,
    });
    setModalOpen(true);
  };

  const handleToggle = async (record) => {
    const nextStatus = !record.is_active;
    try {
      await documentTypeService.update(record.id, { is_active: nextStatus });
      message.success(nextStatus ? 'Tipologia attivata' : 'Tipologia disattivata');
      loadDocumentTypes();
    } catch (e) {
      message.error(formatError(e));
    }
  };

  const handlePermanentDelete = (record) => {
    Modal.confirm({
      title: 'Elimina Tipologia Documento',
      content: `Sei sicuro di voler eliminare definitivamente la tipologia "${record.name}"? Questa azione è irreversibile.`,
      okText: 'Elimina',
      okType: 'danger',
      cancelText: 'Annulla',
      onOk: async () => {
        try {
          await documentTypeService.permanentDelete(record.id);
          message.success('Tipologia eliminata');
          loadDocumentTypes();
        } catch (e) {
          message.error(formatError(e));
        }
      },
    });
  };

  const columns = [
    { title: 'Nome', dataIndex: 'name', key: 'name', sorter: stringSorter('name'), sortDirections: ['ascend', 'descend'] },
    { title: 'Descrizione', dataIndex: 'description', key: 'description', sorter: stringSorter('description'), sortDirections: ['ascend', 'descend'] },
    {
      title: 'Tipi item applicabili',
      dataIndex: 'applicable_item_types',
      key: 'applicable_item_types',
      render: (types) => (types || []).map(t => getItemTypeLabel(t)).join(', '),
    },
    {
      title: 'Modalità Obbligatorietà',
      dataIndex: 'mandatory_mode',
      key: 'mandatory_mode',
      render: (mode, record) => {
        if (mode === 'all') return 'Obbligatorio per tutti';
        if (mode === 'specific') return `Obbligatorio per ${record.mandatory_for_tenants?.length || 0} tenant`;
        return 'Non obbligatorio';
      }
    },
    { title: 'Attivo', dataIndex: 'is_active', key: 'is_active', render: (v) => (v ? 'Sì' : 'No'), sorter: boolSorter('is_active'), sortDirections: ['ascend', 'descend'] },
    {
      title: 'Azioni',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => handleEdit(record)} disabled={!canEdit}>
            Modifica
          </Button>
          <Button size="small" danger={record.is_active} onClick={() => handleToggle(record)} disabled={!canEdit}>
            {record.is_active ? 'Disattiva' : 'Attiva'}
          </Button>
          <Button size="small" danger onClick={() => handlePermanentDelete(record)} disabled={!canEdit}>
            Elimina
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <MainLayout title="Tipologie Documento">
      <Card style={{ marginBottom: 24 }}>
        <Space style={{ width: '100%', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <Title level={2} style={{ margin: 0 }}>Tipologie Documento</Title>
          <Input.Search
            allowClear
            placeholder="Cerca in tutti i campi"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 320 }}
          />
          <Button type="primary" size="large" onClick={() => setModalOpen(true)} disabled={!canEdit}>
            + Nuova Tipologia
          </Button>
        </Space>
      </Card>

      <Card style={{ marginBottom: 24 }}>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={filteredData}
          columns={columns}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={editing ? 'Modifica Tipologia Documento' : 'Nuova Tipologia Documento'}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); setEditing(null); form.resetFields(); setMandatoryMode('none'); }}
        onOk={() => form.submit()}
        okText={editing ? 'Salva' : 'Crea'}
        cancelText="Annulla"
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onSubmit}
          initialValues={{ is_active: true, mandatory_mode: 'none', applicable_item_types: [], mandatory_for_tenants: [] }}
        >
          <Form.Item name="name" label="Nome tipologia" rules={[{ required: true, message: 'Inserisci il nome' }]}>
            <Input placeholder="es: Visura camerale, DURC, ..." />
          </Form.Item>
          <Form.Item name="description" label="Descrizione">
            <Input.TextArea rows={2} placeholder="Descrizione opzionale" />
          </Form.Item>
          <Form.Item name="applicable_item_types" label="Applicabile a tipi item">
            <Checkbox.Group options={getTranslatedItemTypes().map((t) => ({ label: t.label, value: t.value }))} />
          </Form.Item>
          
          <Form.Item name="mandatory_mode" label="Modalità Obbligatorietà">
            <Radio.Group onChange={(e) => setMandatoryMode(e.target.value)}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Radio value="all">
                  <span style={{ fontWeight: 500 }}>Obbligatorio per tutti</span>
                  <p style={{ margin: '4px 0 0 0', color: '#666', fontSize: '12px' }}>
                    Questo documento è obbligatorio per tutti i tenant
                  </p>
                </Radio>
                <Radio value="none">
                  <span style={{ fontWeight: 500 }}>Non obbligatorio</span>
                  <p style={{ margin: '4px 0 0 0', color: '#666', fontSize: '12px' }}>
                    Questo documento è facoltativo per tutti i tenant
                  </p>
                </Radio>
                <Radio value="specific">
                  <span style={{ fontWeight: 500 }}>Obbligatorio per specifici tenant</span>
                  <p style={{ margin: '4px 0 0 0', color: '#666', fontSize: '12px' }}>
                    Seleziona i tenant per cui è obbligatorio
                  </p>
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>

          {mandatoryMode === 'specific' && (
            <Form.Item 
              name="mandatory_for_tenants" 
              label="Tenant per cui è obbligatorio"
              rules={[{ 
                validator: (_, value) => {
                  if (mandatoryMode === 'specific' && (!value || value.length === 0)) {
                    return Promise.reject('Seleziona almeno un tenant');
                  }
                  return Promise.resolve();
                }
              }]}
            >
              <Select
                mode="multiple"
                placeholder="Seleziona uno o più tenant"
                options={tenants.map((t) => ({ label: t.name, value: t.id }))}
              />
            </Form.Item>
          )}

          <Form.Item name="is_active" label="Attivo" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </MainLayout>
  );
}

export default DocumentTypes;
